import { useEffect, useMemo } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { Container, createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import { arSA } from "@mui/material/locale";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import { enUS } from "@mui/x-data-grid";
import Layout from "./main/layout/Layout";
import SignIn from "./main/sign-in/SignIn";
import Exams from "./main/exams/Exams";
import ExamDetails from "./main/exam-details/ExamDetails";
import Questions from "./main/questions/Questions";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { login, logout } from "./redux/userSilce";
import ChangePassword from "./main/change-password/ChangePassword";
import Profile from "./main/profile/Profile";
import Home from "./main/home-pages/home/Home";
import CompletedExams from "./main/completed-exams/CompletedExams";
import CompletedExamDetails from "./main/completed-exam-details/CompletedExamDetails";
import ContactUs from "./main/home-pages/contact-us/ContactUs";
import PrivacyPolicy from "./main/home-pages/privacy-policy/PrivacyPolicy";
import Testimonials from "./main/home-pages/testimonials/Testimonials";
import Album from "./main/home-pages/album/Album";
import News from "./main/home-pages/news/News";
import SingleNews from "./main/home-pages/news/SingleNews";
import AboutUs from "./main/home-pages/about-us/AboutUs";
import ReviewExamDetails from './main/review-exams-details/ReviewExamDetails';
import BoardJoin from "./main/whiteboard/BoardJoin";
// Create rtl cache
const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const cache = createCache({ key: "css" });

function App() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user);
  axios.defaults.headers.common['Authorization'] = `Bearer ${currentUser.token}`;
  axios.defaults.headers.common['Accept-Language'] = i18next.language;

  useEffect(()=>{
    if(currentUser?.token){
      axios
      .get(`${process.env.REACT_APP_API_URL}/profile`)
      .then((response) => {
        dispatch(
          login({
            token: currentUser?.token,
            user: { ...response?.data?.data, token: currentUser?.token },
          })
        );
      })
      .catch((error) => {
        console.log(error);
      });
    }
  },[])

  const theme = useMemo(() => createTheme(
    {
      ...(i18next.language == "ar" && { direction: "rtl" }),
      palette: {
        mode: "light",
        primary: { main: "#2563EB", light: "#60A5FA", dark: "#1D4ED8", contrastText: "#fff" },
        // the accent that carries the "intelligent" side of the product
        secondary: { main: "#7C3AED", light: "#A78BFA", dark: "#6D28D9", contrastText: "#fff" },
        success: { main: "#0E9F6E" },
        warning: { main: "#D97706" },
        error: { main: "#DC2626" },
        background: { default: "#F6F8FC", paper: "#FFFFFF" },
        text: { primary: "#0F172A", secondary: "#64748B" },
        divider: "rgba(15, 23, 42, 0.08)",
        gray: { main: "#64748B" },
        white: { main: "#fff" },
        backgroundGray: { main: "#F6F8FC" },
        lightGray: { main: "#94A3B8" },
        black: { main: "#0F172A" },
        boxBackground: { main: "#0F172A", contrastText: "#fff" },
      },
      shape: { borderRadius: 14 },
      typography: {
        fontFamily: "'Cairo', sans-serif",
        h1: { fontWeight: 800, letterSpacing: "-0.02em" },
        h2: { fontWeight: 800, letterSpacing: "-0.02em" },
        h3: { fontWeight: 800, letterSpacing: "-0.01em" },
        h4: { fontWeight: 800, letterSpacing: "-0.01em" },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
        button: { fontWeight: 700, textTransform: "none" },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: { borderRadius: "100vh", paddingInline: "22px", boxShadow: "none" },
            containedPrimary: {
              background: "linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)",
              boxShadow: "0 8px 20px rgba(37, 99, 235, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #1D4ED8 0%, #6D28D9 100%)",
                boxShadow: "0 10px 26px rgba(37, 99, 235, 0.32)",
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            rounded: { borderRadius: 18 },
            elevation1: { boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 8px 24px rgba(15,23,42,.06)" },
            elevation2: { boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 12px 32px rgba(15,23,42,.08)" },
          },
        },
        MuiChip: { styleOverrides: { root: { fontWeight: 700 } } },
      },
    },
    i18next.language == "ar" ? arSA : enUS
  ), [i18next.language]);

  const page404 = (
    <h1 style={{ textAlign: "center", padding: "250px" }}>
      404 | {t("page_not_found")}
    </h1>
  );
  const page404white = (
    <h1 style={{ textAlign: "center", padding: "250px", color: '#fff' }}>
      404 | {t("page_not_found")}
    </h1>
  );

  useEffect(() => {
    // Add a response interceptor
    axios.interceptors.response.use(
      function (config) {
        // Do something with response
        return config;
      },
      function (error) {
        if (error?.response?.status == 401) {
          dispatch(logout());
          window.location.href = `${window.location.origin}/sign-in`;
          delete axios.defaults.headers.common["Authorization"];
          axios.get(`${process.env.REACT_APP_API_URL}/auth/logout`);
        }
        return Promise.reject(error);
      }
    );
  }, []);

  useEffect(() => {
    if (i18next.language == "ar") {
      document.dir = "rtl";
    } else {
      document.dir = "ltr";
    }
  }, [i18next.language]);

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Layout/>,
      children: [
        {
          index: true,
          element: <Home/>
        },
        {
          path: 'about-us',
          element: <AboutUs/>
        },
        {
          path: 'contact-us',
          element: <ContactUs/>
        },
        {
          path: 'privacy-usage-policy',
          element: <PrivacyPolicy/>
        },
        {
          path: 'album',
          element: <Album/>
        },
        {
          path: 'news',
          element: <News/>
        },
        {
          path: 'news/:newsId',
          element: <SingleNews/>
        },
        // {
        //   path: 'testimonials',
        //   element: <Testimonials/>
        // },
        {
          path: 'sign-in',
          element: currentUser.token ? <Navigate to='/'/> : <SignIn/>
        },
        {
          path: 'sign-up',
          // student self-registration is closed: the teacher adds their students
          element: <Navigate to='/sign-in'/>
        },
        {
          path: 'exams',
          element: currentUser.token ? <Exams/> : <Navigate to='/sign-in'/>
        },
        {
          path: 'exams/:examId',
          element: currentUser.token ? <ExamDetails/> : <Navigate to='/sign-in'/>
        },
        {
          path: 'exams/:examId/questions',
          element: currentUser.token ? <Questions/> : <Navigate to='/sign-in'/>
        },
        {
          path: 'completed-exams',
          element: currentUser.token ? <CompletedExams/> : <Navigate to='/sign-in'/>
        },
        {
          path: 'completed-exams/:examId',
          element: currentUser.token ? <CompletedExamDetails/> : <Navigate to='/sign-in'/>
        },
        {
          path: 'change-password',
          element: currentUser.token ? <ChangePassword/> : <Navigate to='/sign-in'/>
        },
        {
          path: 'profile',
          element: currentUser.token ? <Profile/> : <Navigate to='/sign-in'/>
        },
       { path:'/exams/:examId/review',
        element: currentUser.token ? <ReviewExamDetails/> : <Navigate to='/sign-in'/>
       },{
          path: "*",
          element: page404white,
        },
      ]
    },
    {
      // live whiteboard: standalone full-screen page (no site header/footer);
      // BoardJoin handles auth by itself
      path: "/board/:code",
      element: <BoardJoin/>
    },
    {
      path: "*",
      element: page404,
    },
  ]);

  return (
    <CacheProvider
      {...(i18next.language == "ar" ? { value: cacheRtl } : { value: cache })}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
